import React, { useState } from 'react';

import { Radio } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import { FlatList } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { DeliveryTerms } from 'components/address-modal/delivery-terms';
import { IsoCountryCode } from 'generated/graphql-gateway';
import { StoreChangeDisclaimer } from 'pages/store-locator/components';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { isWeb } from 'utils/environment';
import { IPlaceAddress } from 'utils/geolocation';

import { DeliveryAddressItem } from './delivery-address-item';
import { ButtonContainer, DeliveryInfoContainer } from './styled';
import { IDeliveryAddressesProps } from './types';

const castCountryType = (country: IsoCountryCode | null | undefined): string | undefined =>
  (country && (IsoCountryCode[country] as string)) || undefined;

export const DeliveryAddresses: React.FC<React.PropsWithChildren<IDeliveryAddressesProps>> = ({
  addresses,
  onCreate,
  setAddress,
  isLoading = false,
  saveDeliveryAddress: _saveDeliveryAddress,
  editDeliveryAddress: _editDeliveryAddress,
  removeDeliveryAddress: _removeDeliveryAddress,
}) => {
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useState('');

  const onSelectAddress = async () => {
    const address = addresses[+selected];
    logEvent(CustomEventNames.SELECT_DELIVERY_SAVED_ADDRESS, EventTypes.Other);

    const selectedAddressComponents: IPlaceAddress = {
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || '',
      city: address.city,
      state: address.state || '',
      streetNumber: address.streetNumber || '',
      route: address.route || '',
      zip: address.zip || '',
      country: castCountryType(address.country),
      phoneNumber: address.phoneNumber || '',
    };

    let addressCoordinates;
    if (address.latitude && address.longitude) {
      addressCoordinates = {
        lat: address.latitude,
        lng: address.longitude,
      };
    }

    const selectedPhoneNumber = address.phoneNumber;
    setAddress({
      address: selectedAddressComponents,
      phoneNumber: selectedPhoneNumber,
      coordinates: addressCoordinates,
    });
  };

  const RadioGroup: React.FC<React.PropsWithChildren> = ({ children }) => (
    <Radio.Group
      name="delivery-addresses-radio"
      testID="delivery-addresses"
      alignItems="stretch"
      value={selected.toString()}
      onChange={value => setSelected(value)}
      focusable={!isWeb} // Prevents focus on web, which causes the page to scroll to the focused element.
    >
      {children}
    </Radio.Group>
  );

  const DeliveryAddressesButtons = () => (
    <ButtonContainer>
      <ActionButton
        testID="select-delivery-address"
        disabled={isLoading || selected === ''}
        isLoading={isLoading}
        onPress={onSelectAddress}
        fullWidth
        mb="$4"
      >
        {formatMessage({ id: 'deliverHere' })}
      </ActionButton>
      <ActionButton
        testID="create-delivery-address"
        disabled={isLoading}
        onPress={onCreate}
        fullWidth
        variant={ActionButtonVariants.OUTLINE}
      >
        {formatMessage({ id: 'deliveryAddNewAddress' })}
      </ActionButton>
    </ButtonContainer>
  );

  return (
    <RadioGroup>
      <FlatList
        data={addresses}
        keyExtractor={(_, index) => index.toString()}
        renderItem={({ item, index }) => (
          <DeliveryAddressItem address={item} value={index.toString()} />
        )}
        ListFooterComponent={() => (
          <>
            <DeliveryInfoContainer>
              <DeliveryTerms />
              <StoreChangeDisclaimer />
            </DeliveryInfoContainer>
            <DeliveryAddressesButtons />
          </>
        )}
      />
    </RadioGroup>
  );
};
